import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';

import { API } from '@/utils/api';

export default function ResetPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, { email });
      setSent(true);
      toast.success('Sprawdź swoją skrzynkę email!');
    } catch (err) {
      // Always show success to prevent email enumeration
      setSent(true);
      toast.success('Sprawdź swoją skrzynkę email!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080810] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Cosmic Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-950/30 via-[#080810] to-[#080810]" />
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-orange-600/10 rounded-full blur-[180px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-orange-500/10 rounded-full blur-[150px]" />
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `radial-gradient(2px 2px at 20px 30px, white, transparent),
                           radial-gradient(2px 2px at 40px 70px, rgba(255,255,255,0.8), transparent),
                           radial-gradient(1px 1px at 90px 40px, white, transparent)`,
          backgroundSize: '200px 150px'
        }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo & Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <img src="/logo.svg" alt="ApisAI" className="h-10" />
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2">{t('resetPassword.title', 'Zresetuj hasło')}</h1>
          <p className="text-zinc-400">{t('resetPassword.subtitle', 'Wyślemy Ci link do ustawienia nowego hasła')}</p>
        </div>

        {/* Glass Card */}
        <div className="backdrop-blur-xl bg-white/[0.03] border border-white/10 rounded-2xl p-8 shadow-[0_0_60px_rgba(168,85,247,0.1)]">
          {sent ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-6"
            >
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">{t('resetPassword.sent', 'Email wysłany!')}</h2>
              <p className="text-zinc-400 mb-6">
                Sprawdź swoją skrzynkę <span className="text-white font-medium">{email}</span>
              </p>
              <p className="text-zinc-500 text-sm mb-6">
                Nie widzisz emaila? Sprawdź folder spam.
              </p>
              <Button
                variant="outline"
                onClick={() => setSent(false)}
                className="border-white/20 text-gray-300 hover:bg-white/10"
              >
                Wyślij ponownie
              </Button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300 text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.emailPlaceholder')}
                    required
                    className="pl-11 bg-white/5 border-white/10 focus:border-orange-500/50 focus:bg-white/10 rounded-xl h-12 text-white placeholder:text-zinc-500 transition-all"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-lg hover:shadow-orange-500/30 text-white font-semibold transition-all"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Mail className="w-5 h-5 mr-2" />
                    Wyślij link resetujący
                  </>
                )}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/logowanie" className="text-zinc-400 hover:text-orange-300 transition-colors text-sm inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Wróć do logowania
            </Link>
          </div>
        </div>
        
        {/* Back to home link */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-zinc-500 hover:text-gray-300 text-sm transition-colors">
            ← {t('common.backToHome')}
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
